import {
  ConnectedPosition,
  Overlay,
  OverlayConfig,
  OverlayRef,
  PositionStrategy
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Injector,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewContainerRef,
  ViewEncapsulation,
  WritableSignal,
  computed,
  forwardRef,
  input,
  output,
  signal,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Observable,
  debounceTime,
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  of,
  switchMap,
  take,
  tap
} from 'rxjs';
import { SelectOptionComponent } from './select-option.component';
import { at, isArray, isEmpty, isEqual, remove } from 'lodash-es';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { SelectedOption } from './select.models';
import { CommonModule } from '@angular/common';
import { normalizeString, convertToString } from '~ui-kit/core/utils';

@Component({
  selector: 'select-field',
  standalone: true,
  host: {
    role: 'combobox',
    'aria-autocomplete': 'none',
    'aria-haspopup': 'listbox',
    '[attr.id]': 'id() || null',
    '[attr.aria-expanded]': 'panelOpen()',
    '[attr.aria-label]': 'ariaLabel() || null',
    '[attr.aria-required]': 'isRequired().toString()',
    '[attr.aria-disabled]': 'isDisabled().toString()',
    '[attr.aria-readonly]': 'readonly().toString()',
    '[attr.aria-invalid]': 'isInvalid().toString()',
    class: 'select-field',
    '[class.select-field--expanded]': 'panelOpen()',
    '[class.input-filled]': 'triggerLabel()',
    '[class.input-disabled]': 'isDisabled()',
    '[class.input-readonly]': 'readonly()'
  },
  template: `<div class="select-field__wrapper">
    <button
      type="button"
      class="select-trigger"
      tabindex="-1"
      [attr.disabled]="isDisabled() || readonly() || null"
      #triggerRef
    >
      @if (!triggerLabel()) {
        <span class="select-trigger__placeholder">{{ placeholder() }}</span>
      } @else {
        <span class="select-trigger__label">{{ triggerLabel() }}</span>
      }
      <span class="select-trigger__chevron">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="8.758"
          height="5.379"
          viewBox="0 0 8.758 5.379"
        >
          <path
            d="M666.168,833.138l2.965-2.965,2.965,2.965"
            transform="translate(-664.754 -829.173)"
            fill="none"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
          />
        </svg>
      </span>
    </button>
    <ng-template #panelTpl>
      <div
        class="select-panel"
        [class]="additionalClass() || ''"
        [style.min-width.px]="hostFormField ? hostFormField.clientWidth : 0"
      >
        @if (allowSearch()) {
          <div class="select-panel__search-wrapper">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="21.691"
              height="21.688"
              viewBox="0 0 21.691 21.688"
            >
              <path
                id="Tracé_2"
                data-name="Tracé 2"
                d="M21.489,20.509,15.889,15a8.857,8.857,0,0,0,2.367-6.02A9.054,9.054,0,0,0,9.131,0,9.053,9.053,0,0,0,.006,8.98a9.053,9.053,0,0,0,9.125,8.98,9.177,9.177,0,0,0,5.743-2L20.5,21.486a.71.71,0,0,0,.992,0,.684.684,0,0,0,0-.977ZM9.131,16.578A7.66,7.66,0,0,1,1.41,8.98a7.66,7.66,0,0,1,7.721-7.6,7.66,7.66,0,0,1,7.721,7.6A7.66,7.66,0,0,1,9.131,16.578Z"
                transform="translate(-0.006 0)"
                fill="#199e46"
              />
            </svg>
            <input
              type="text"
              [id]="uniqueId() + 'search-option'"
              [attr.placeholder]="searchPlaceholder()"
              autofocus="true"
              [formControl]="searchTerm"
            />
            @if (allowSearchClearButton()) {
              <button
                class="search-clear-button"
                type="button"
                (click)="clearSearch()"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="15"
                  height="15"
                  viewBox="0 0 15 15"
                >
                  <g
                    id="Groupe_19512"
                    data-name="Groupe 19512"
                    transform="translate(-952.057 -423.5)"
                  >
                    <rect
                      id="Rectangle_18338"
                      data-name="Rectangle 18338"
                      width="15"
                      height="15"
                      rx="7.5"
                      transform="translate(952.057 423.5)"
                      fill="rgba(69,69,69,0.1)"
                    />
                    <path
                      id="close"
                      d="M3.55,3.136,5.886.8a.389.389,0,0,0-.55-.55L3,2.586.664.25A.389.389,0,0,0,.114.8L2.45,3.136.114,5.473a.389.389,0,1,0,.55.55L3,3.686,5.337,6.022a.389.389,0,1,0,.55-.55Zm0,0"
                      transform="translate(956.557 428.364)"
                      fill="#454545"
                    />
                  </g>
                </svg>
              </button>
            }
          </div>
        }
        <ul class="select-option-list" role="listbox">
          @if (loading()) {
            <li (click)="_closePanel()" class="select-option">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 448 512"
                fill="#199e46"
              >
                <path
                  d="M352 256C352 238.3 366.3 224 384 224C401.7 224 416 238.3 416 256C416 273.7 401.7 288 384 288C366.3 288 352 273.7 352 256zM192 256C192 238.3 206.3 224 224 224C241.7 224 256 238.3 256 256C256 273.7 241.7 288 224 288C206.3 288 192 273.7 192 256zM96 256C96 273.7 81.67 288 64 288C46.33 288 32 273.7 32 256C32 238.3 46.33 224 64 224C81.67 224 96 238.3 96 256z"
                />
              </svg>
            </li>
          }
          @if (errored() && !loading()) {
            <li (click)="emitReload()" class="select-option">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 512 512"
                fill="#199e46"
              >
                <path
                  d="M32.05 256.1c0 123.5 100.4 223.9 223.9 223.9c104.4 0 180-76.8 180-95.94c0-9.377-7.705-16.03-15.98-16.03C399.5 367.1 369.3 448 256 448c-105.9 0-192-86.13-192-192s86.13-192 192-192c75.11 0 123.1 44.4 124.2 45.15l-55.54 55.54c-3.055 3.055-4.681 7.16-4.681 11.33C320 184.9 327.2 192 336 192h128C472.8 192 480 184.8 480 176v-128c0-8.606-6.901-15.97-15.97-15.97c-4.171 0-8.291 1.598-11.35 4.653L402.1 87.32c-8.646-13.15-70.2-55.25-146.1-55.25C132.4 32.07 32.05 132.6 32.05 256.1zM374.6 160L448 86.63V160H374.6z"
                />
              </svg>
            </li>
          }
          <ng-content select="select-option"></ng-content>
          @if (filteredOptionsIsEmpty()) {
            <li class="select-option-list__no-option">
              {{ noResultFeedback() }}
            </li>
          }
        </ul>
      </div>
    </ng-template>
  </div>`,
  imports: [CommonModule, ReactiveFormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => SelectComponent)
    }
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectComponent
  implements
    OnChanges,
    OnInit,
    AfterViewInit,
    AfterContentInit,
    OnDestroy,
    ControlValueAccessor
{
  @Output() readonly valueChange: EventEmitter<
    SelectedOption | Array<SelectedOption>
  > = new EventEmitter<SelectedOption | Array<SelectedOption>>();

  @Output() readonly openedChange: EventEmitter<boolean> =
    new EventEmitter<boolean>();

  @Output() readonly opened: Observable<void> = this.openedChange.pipe(
    filter(o => o),
    map(() => {})
  );

  @Output() readonly closed: Observable<void> = this.openedChange.pipe(
    filter(o => !o),
    map(() => {})
  );

  reload = output<void>();

  placeholder = input<string | undefined | null>('Choisissez une option');
  uniqueId = input.required<string>();
  ariaLabel = input('');
  disabled = input(false);
  readonly = input(false);
  multiple = input(false);
  allowSearch = input(false);
  loading = input(false);
  errored = input(false);
  additionalClass = input<string[] | undefined>();
  panelClass = input<string | string[]>('');
  searchPlaceholder = input<string | undefined | null>(
    'Rechercher parmi les options'
  );

  propertiePathsToLookThrough = input<string[] | undefined | null>();
  allowSearchClearButton = input(true);
  noResultFeedback = input<string | undefined | null>('Aucun résultat');

  panelTemplate = viewChild<TemplateRef<unknown>>('panelTpl');

  triggerElement = viewChild<ElementRef<HTMLButtonElement>>('triggerRef');

  @ContentChildren(SelectOptionComponent, {
    descendants: true
  })
  optionQueryList: QueryList<SelectOptionComponent> | undefined;

  searchTerm = new FormControl<string>('');

  panelOpen = signal(false);
  selectedOption = signal<SelectedOption | undefined>(undefined);
  selectedOptions = signal<SelectedOption[]>([]);
  id = signal('');
  isInvalid = signal(false);
  isDisabled = signal(false);
  isRequired = signal(false);
  triggerLabel = signal<string | undefined>(undefined);
  options: WritableSignal<SelectOptionComponent[]> = signal([]);
  filteredOptions: WritableSignal<SelectOptionComponent[]> = signal([]);

  filteredOptionsIsEmpty = computed(() => isEmpty(this.filteredOptions()));

  onChange: (value: unknown) => void = () => {};

  onTouched: () => void = () => {};

  private _panelPortal: TemplatePortal<unknown> | undefined;

  private _overlayRef: OverlayRef | undefined;

  private get _host(): HTMLElement {
    return this._vcr.element.nativeElement as HTMLElement;
  }

  get hostFormField(): HTMLElement | null {
    return this._host.closest('.form-field') as HTMLElement | null;
  }

  private _positions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top'
    },
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top'
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
      panelClass: 'select-panel-above'
    },
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
      panelClass: 'select-panel-above'
    }
  ];

  private get _ngControl(): NgControl | null {
    const ngControl = this._injector.get(NgControl, undefined, {
      optional: true,
      self: true
    });

    return ngControl;
  }

  constructor(
    private _overlay: Overlay,
    private _destroyRef: DestroyRef,
    private _vcr: ViewContainerRef,
    private _injector: Injector
  ) {}

  ngOnInit(): void {
    if (this._ngControl) {
      this._ngControl.valueAccessor = this;
      this.isRequired.set(
        this._ngControl.control?.hasValidator(Validators.required) ?? false
      );
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('disabled' in changes) {
      this.isDisabled.set(this.disabled());
    }

    if ('readonly' in changes) {
      this._setFormFieldCursorStyle();
    }

    if ('uniqueId' in changes) {
      if (!this.uniqueId().length || this.uniqueId().includes(' ')) {
        throw new Error('uniqueId must not be empty or contain spaces.');
      } else {
        this.id.set(this.uniqueId());
      }
    }

    if ('multiple' in changes) {
      this._manageOptionsMultipleProperty(this.multiple());
      this.selectedOption.set(undefined);
      this.selectedOptions.set([]);
      this._selectTheRightOption(false);
    }
  }

  ngAfterViewInit(): void {
    if (!this.hostFormField) {
      throw new Error(
        'To ensure proper functionality, SelectComponent should be hosted within a FormFieldComponent.'
      );
    }

    this._panelPortal = new TemplatePortal(this.panelTemplate()!, this._vcr);
    this._handleTriggerClick();
    this._setFormFieldCursorStyle();
  }

  ngAfterContentInit(): void {
    this.optionQueryList?.changes
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((list: QueryList<SelectOptionComponent>) => {
        this.options.set(list.toArray());
        this.filteredOptions.set(this.options());
        this._optionsManager();
        this._manageOptionsMultipleProperty(this.multiple());
        this._selectTheRightOption(false);
      });

    this.options.set(this.optionQueryList?.toArray() || []);
    this.filteredOptions.set(this.options());
    this._optionsManager();
    this._manageOptionsMultipleProperty(this.multiple());
    this._selectTheRightOption(false);
    this._handleSearchTermControl();
  }

  ngOnDestroy(): void {
    this._closePanel();
  }

  click(): void {
    this.triggerElement()!.nativeElement.click();
  }

  emitReload(): void {
    this.reload.emit();
    this._closePanel();
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  // eslint-disable-next-line unused-imports/no-unused-vars
  writeValue(value: unknown): void {
    this._selectTheRightOption(false);
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  clearSearch(): void {
    this.searchTerm.setValue('');
  }

  private _onOpenPanel(): void {
    if (this._overlayRef || !this.hostFormField) return;
    const formFieldWrapper = this.hostFormField.querySelector(
      '.form-field__wrapper'
    ) as HTMLElement | null;

    if (!formFieldWrapper) return;

    this._overlayRef = this._overlay.create(
      this._getOverlayConfig(formFieldWrapper)
    );
    this._overlayRef.addPanelClass(this.panelClass());
    this._overlayRef.attach(this._panelPortal);
    this._handleOverlayBackdropClick();
    this.panelOpen.set(true);
    this._emitOpenChange();
    setTimeout(() => {
      this._scrollToSelectedOption();
    }, 50);
  }

  protected _closePanel(): void {
    this._overlayRef?.detach();
    this._overlayRef?.dispose();
    this._overlayRef = undefined;
    this.panelOpen.set(false);
    this._removeClassFromHostElement('input-focused');
    this._emitOpenChange();
    this.clearSearch();
  }

  private _handleTriggerClick(): void {
    fromEvent(this.triggerElement()!.nativeElement, 'click')
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        switchMap(() => {
          this._onOpenPanel();
          this._addClassToHostElement('input-focused');
          this.onTouched();
          return of(undefined);
        })
      )
      .subscribe();
  }

  private _handleOverlayBackdropClick(): void {
    this._overlayRef
      ?.backdropClick()
      .pipe(
        take(1),
        tap({
          next: () => {
            this._closePanel();
          }
        })
      )
      .subscribe();
  }

  private _optionsManager(): void {
    this.filteredOptions().forEach(option => {
      fromEvent(option.host, 'click')
        .pipe(
          takeUntilDestroyed(option.destroyRef),
          tap({
            next: () => {
              this.multiple()
                ? this._selectOptions(option)
                : this._selectOption(option);
            }
          })
        )
        .subscribe();
    });
  }

  private _selectOption(selectOption: SelectOptionComponent): void {
    const value = selectOption.formattedValue();
    if (!isEqual(this.selectedOption()?.value, value)) {
      this.selectedOption.set({
        label: selectOption.host.innerText || '',
        value
      });
      this.triggerLabel.set(this.selectedOption()?.label);
      this._emitSelection();
      this.onChange(value);

      this.options().forEach(option => {
        option.isSelected.set(option.uniqueId === selectOption.uniqueId);
      });
    }
    this._closePanel();
  }

  private _selectOptions(selectOption: SelectOptionComponent): void {
    const selectedOptions = this.selectedOptions();
    selectOption.isSelected.set(!selectOption.isSelected());
    selectOption.detectChange();
    const value = selectOption.formattedValue();

    if (selectOption.isSelected()) {
      selectedOptions.push({
        label: selectOption.host.innerText,
        value
      });
    } else {
      remove(selectedOptions, o => isEqual(value, o.value));
    }

    const label = selectedOptions.map(o => o.label).join(', ');
    this.selectedOptions.set(selectedOptions);
    this.triggerLabel.set(label);
    this._emitSelection();
    this.onChange(selectedOptions.map(o => o.value));
  }

  private _selectTheRightOption(emitSelection: boolean): void {
    const selectedOptions = this.options().filter(option =>
      option.isSelected()
    );

    if (this._ngControl) {
      const control = this._ngControl.control;
      const defaultValue = control?.value;
      this._unCheckAllOptions(selectedOptions);

      if (this.multiple()) {
        if (isArray(defaultValue)) {
          const selectedOpts = this.options()
            .map(o => {
              o.isSelected.set(defaultValue.includes(o.formattedValue()));
              o.detectChange();
              return o;
            })
            .filter(o => o.isSelected())
            .map(o => ({
              label: o.host.innerText,
              value: o.formattedValue()
            }));

          this.selectedOptions.set(selectedOpts);
        }
      } else {
        const selectedOpt = this.options()
          .map(o => {
            o.isSelected.set(o.formattedValue() === defaultValue);
            return o;
          })
          .filter(o => o.isSelected())
          .map(o => ({
            value: o.formattedValue(),
            label: o.host.innerText
          }))[0];

        this.selectedOption.set(selectedOpt);
      }
    } else {
      if (this.multiple()) {
        const selectedOpts = selectedOptions.map(o => ({
          label: o.host.innerText,
          value: o.formattedValue()
        }));
        this.selectedOptions.set(selectedOpts);
      } else {
        const selectedOpt = selectedOptions.pop();

        if (selectedOpt) {
          this._unCheckAllOptions(selectedOptions);
          this.selectedOption.set({
            label: selectedOpt.host.innerText,
            value: selectedOpt.formattedValue()
          });
        }
      }
    }

    this.multiple()
      ? this.triggerLabel.set(
          this.selectedOptions()
            .map(o => o.label)
            .join(', ')
        )
      : this.triggerLabel.set(this.selectedOption()?.label);

    if (emitSelection) {
      this._emitSelection();
    }
  }

  private _getOverlayConfig(
    origin: HTMLElement,
    width?: number
  ): OverlayConfig {
    return new OverlayConfig({
      width,
      hasBackdrop: true,
      backdropClass: 'select-panel-backdrop',
      positionStrategy: this._getOverlayPosition(origin),
      scrollStrategy: this._overlay.scrollStrategies.reposition({
        autoClose: true
      })
    });
  }

  private _getOverlayPosition(origin: HTMLElement): PositionStrategy {
    const positionStrategy = this._overlay
      .position()
      .flexibleConnectedTo(origin)
      .withPositions(this._positions)
      .withPush(false);

    return positionStrategy;
  }

  private _addClassToHostElement(value: string): void {
    this._host.classList.add(value);
  }

  private _removeClassFromHostElement(value: string): void {
    this._host.classList.remove(value);
  }

  private _unCheckAllOptions(options: SelectOptionComponent[]): void {
    options.forEach(option => {
      option.isSelected.set(false);
      option.detectChange();
    });
  }

  private _emitSelection(): void {
    if (this.multiple()) {
      this.valueChange.emit(this.selectedOptions());
    } else {
      if (!this.selectedOption()) return;
      this.valueChange.emit(this.selectedOption());
    }
  }

  private _emitOpenChange(): void {
    this.openedChange.emit(this.panelOpen());
  }

  private _setFormFieldCursorStyle(): void {
    if (!this.hostFormField) return;
    (
      this.hostFormField.querySelector('.form-field__wrapper') as HTMLElement
    ).style.cursor = this.readonly() ? 'default' : 'pointer';
  }

  private _manageOptionsMultipleProperty(multiple: boolean): void {
    this.options().forEach(option => {
      option.isMultiple.set(multiple);
      option.detectChange();
    });
  }

  private _scrollToSelectedOption(): void {
    const option = this.options().find(o => o.isSelected());
    if (!option) return;
    const optionHeight = option.host.offsetHeight;
    const optionTop = option.host.offsetTop - 26;
    const optionParent = option.host.closest(
      '.select-option-list'
    ) as HTMLElement;
    const optionParentHeight = optionParent.clientHeight;
    const distance = optionTop + optionHeight - optionParentHeight;

    if (distance > 0) {
      optionParent.scrollTop = distance;
    }
  }

  private _handleSearchTermControl(): void {
    this.searchTerm.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this._destroyRef),
        tap(term => this._filterOptions(term || ''))
      )
      .subscribe();
  }

  private _filterOptions(term: string): void {
    const filteredOptions = this.options()
      .map(option => {
        const isHidden = this._shouldHideOption(option, term);
        if (option.isHidden() !== isHidden) {
          option.isHidden.set(isHidden);
          option.detectChange();
        }

        return option;
      })
      .filter(option => !option.isHidden());

    this.filteredOptions.set(filteredOptions);
  }

  private _shouldHideOption(
    option: SelectOptionComponent,
    term: string
  ): boolean {
    const paths = this.propertiePathsToLookThrough() || [];
    if (!term) return false;
    if (paths.length) {
      return !this._isTermInsidePathsValue(option, paths, term);
    }
    return !this._isTermInsideValueOrLabel(option, term);
  }

  private _isTermInsideValueOrLabel(
    option: SelectOptionComponent,
    term: string
  ): boolean {
    const value = normalizeString(option.formattedValue());
    const label = normalizeString(option.host.innerText);
    const normalizedTerm = normalizeString(term);

    return value.includes(normalizedTerm) || label.includes(normalizedTerm);
  }

  private _isTermInsidePathsValue(
    option: SelectOptionComponent,
    paths: string[],
    term: string
  ): boolean {
    const value = option.value();
    const normalizedTerm = normalizeString(term);

    if (
      value &&
      typeof value === 'object' &&
      !isArray(value) &&
      !isEmpty(value)
    ) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const pathsValue = at(value as any, paths);
      return pathsValue.some(value => {
        const normalizedValue = normalizeString(
          convertToString(value, { clean: true, removeDoubleQuotes: true })
        );

        return normalizedValue.includes(normalizedTerm);
      });
    } else {
      return false;
    }
  }
}
